<template>
    <div class="modal-content" ref="container">
        <div class="modal-header">
            <h2 class="modal-title">{{ status == "submitted" ? "Status Updated" : "Confirm Status Change" }}</h2>
            <button type="button" class="close icon icon-close" aria-hidden="true" @click="close" autofocus :disabled="status == 'submitting'" aria-label="Close" />
        </div>
        <div class="modal-body">
            <template v-if="status == 'pending'">
                <div v-if="messages.length > 0" class="alert alert-danger">
                    <span class="sr-only">Validation:</span>
                    <ul>
                        <li v-for="message in messages">{{ message }}</li>
                    </ul>
                </div>
                <div class="form-group">
                    <label for="currentStatusLabel" describedby-text
                        >Current Status: <span class="p-1">{{ currentStatusLabel }}</span></label
                    >
                    <br />
                    <label :for="id + '_Status'">Change status to</label>
                    <select name="status" :id="id + '_Status'" class="form-control" v-model="newStatus">
                        <option :value="possibleStatus.value" v-for="possibleStatus in possibleStatusesExcludingIgnored">{{ possibleStatus.text || possibleStatus.value }}{{ isStatusDisabled(possibleStatus) ? " (Unavailable)" : "" }}</option>
                    </select>
                </div>
                <div class="form-group">
                    <label for="ReturnComment">Comment:</label>
                    <textarea row="3" v-model="comment" id="StatusChangeComment" class="form-control" placeholder="A comment is required" />
                </div>
            </template>
            <div v-else-if="status == 'submitting'">
                <LoadingWheel :width="100" />
            </div>
            <template v-else-if="status == 'submitted'">
                <span class="action-success-icon icon icon-check"></span>
                <p describedby-text class="action-success-text">Status updated successfully.</p>
            </template>
            <div v-else-if="status == 'broken'">
                <LoadingWheel :width="100" />
            </div>
        </div>
        <div class="modal-footer">
            <template v-if="status != 'submitted'">
                <button id="statusChangeBtn" class="btn btn-primary" @click="submit()" :disabled="status == 'submitting'">Change Status</button>
                <button id="cancelBtn" class="btn btn-outline-secondary" @click="close" :disabled="status == 'submitting'">Cancel</button>
            </template>
            <button id="cancelBtn" class="btn btn-outline-secondary" @click="close" v-else>Close</button>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, SetupContext } from "vue";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import type { Status as FormDefStatus } from "@cs-ts/status";
import { StandardFormStatus } from "@cs/StandardFormStatus";
import { usePageStore } from "../pageState/store";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import generateGuid from "@/lib/generateGuid";
type Props = {
    returnFunction: (newStatus: string, comment: string) => Promise<{ success: boolean; messages: string[] }>;
} & DialogProps;
export default defineComponent({
    components: {
        LoadingWheel,
    },
    props: ["returnFunction", "triggerElement"],
    setup(props: Props, { emit }: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef, props);

        const status = ref("pending" as "pending" | "submitting" | "submitted");
        const newStatus = ref(null as null | string);
        const comment = ref(null as null | string);
        const messages = ref([] as string[]);
        const id = generateGuid();

        const pageStore = usePageStore();

        const possibleStatusesExcludingIgnored = computed(() => {
            return pageStore.possibleStatuses.filter(i => ![StandardFormStatus.Returned, StandardFormStatus.Deleted, StandardFormStatus.InProgress, StandardFormStatus.Submitting].some(j => j == i.value));
        });

        const currentStatusLabel = computed(() => pageStore.currentStatusLabel);
        const anonymousUser = computed(() => pageStore.currentStatusLabel);

        const isStatusDisabled = (status: FormDefStatus) => {
            if (status.value == StandardFormStatus.Returned && anonymousUser.value) {
                return true;
            }
            return false;
        };

        const submit = async () => {
            if (newStatus.value == null || comment.value == null || comment.value.trim().length == 0) {
                messages.value = ["Status and Comment are required"];
                return;
            }
            status.value = "submitting";
            const result = await props.returnFunction(newStatus.value, comment.value);
            if (result.success) {
                status.value = "submitted";
            } else {
                status.value = "pending";
                messages.value = result.messages;
            }
        };

        return {
            container: containerRef,
            id,
            status,
            currentStatusLabel,
            isStatusDisabled,
            possibleStatusesExcludingIgnored,
            messages,
            comment,
            submit,
            newStatus,
            close: () => emit("close"),
        };
    },
});
</script>
